import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HlsStream, RtspStream } from '@casa/video';
import type { TileOf } from '@casa/model';
import { concatBytes } from '@casa/util';
import { lastValue } from '../../values/readings.ts';
import { useAppForeground, useScreenActive } from '../../view/screen_activity.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';

/**
 * The legacy `video` tile: the camera of the tile's `url`, played with the RTSP client of
 * `@casa/video` over `net.tcp`, with its 30 s stall watchdog; or the stream a source gives, an
 * HLS playlist, played with its HLS player over `net.http` (video-hls.md VH-2). It plays only while its screen is
 * shown; the legacy app kept LibVLC playing. The picture keeps its aspect ratio, as VLC's did.
 *
 * A tap shows the picture over the whole app until the next tap or Escape (the owner, issue #2).
 * The same canvas moves there and back, so the stream goes on without starting again. In edit mode
 * the edit layer takes the taps. On an upright screen a landscape picture is turned a quarter, to
 * fill it when the phone is turned (the owner, issue #2).
 */
export function VideoTileView({ tile }: { tile: TileOf<'video'> }) : ReactNode {
    const screenShown = useScreenActive();
    const foreground = useAppForeground();
    const active = screenShown && foreground;
    const environment = useTileEnvironment();
    const { bridge } = environment;
    const { options } = tile;
    // The second way (VH-2): the source's reading, the stream's address.
    const sourceName = 'source' in options && tile.dataSources[options.source] !== undefined ? options.source : undefined;
    const reading = lastValue(environment.useReadings(tile, sourceName));
    const hls = 'url' in options ? undefined : typeof reading === 'string' && /^https?:\/\/\S+$/i.test(reading.trim()) ? reading.trim() : undefined;
    const url = 'url' in options ? options.url : hls;
    const hostRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<RtspStream | HlsStream | null>(null);
    const [isMaximized, setMaximized] = useState(false);
    const [isTurned, setTurned] = useState(false);

    // Where the canvas belongs now: in the tile, or over the whole app.
    const placeCanvas = () : void => {
        const canvas = canvasRef.current;
        const container = (isMaximized ? overlayRef.current : null) ?? hostRef.current;
        if (canvas && container && canvas.parentElement !== container) {
            container.replaceChildren(canvas);
        }
    };

    useEffect(() => {
        const host = hostRef.current;
        const net = bridge?.net;
        if (!active || !host || !net || url === undefined) {
            return;
        }

        // A fresh canvas for every start: a Worker pipeline takes its canvas over for good.
        const canvas = document.createElement('canvas');
        canvas.style.cssText = 'display: block; width: 100%; height: 100%; object-fit: contain';
        canvasRef.current = canvas;
        placeCanvas();
        const log = (line: string) : void => console.info(`${tile.label ?? 'video'}: ${line}`);
        const stream = hls === undefined
            ? new RtspStream({ url, openChannel: (address, port) => net.tcp.open(address, port), canvas, log })
            : new HlsStream({
                url,
                fetch: async (address, timeoutMs) => {
                    const response = await net.http.request({ method: 'GET', url: address, timeoutMs });
                    const chunks: Uint8Array[] = [];
                    for await (const chunk of response.body) {
                        chunks.push(chunk);
                    }
                    return { status: response.status, body: concatBytes(chunks) };
                },
                canvas,
                log,
            });
        const start = async () : Promise<void> => {
            try {
                await stream.start();
            } catch (error) {
                console.error(`${tile.label ?? 'video'}: the stream could not start`, error);
            }
        };
        streamRef.current = stream;
        void start();

        return () => {
            void stream.stop();
            canvas.remove();
            canvasRef.current = null;
            streamRef.current = null;
        };
        // placeCanvas reads the refs and isMaximized when it runs; moving is the layout effect's job.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, url, hls, tile.label, bridge]);

    // Off screen or in the background, the picture goes back to its tile.
    useEffect(() => {
        if (!active) {
            setMaximized(false);
        }
    }, [active]);

    useLayoutEffect(placeCanvas);

    // Full screen on an upright window, a landscape picture is turned a quarter to the right. Until
    // the first picture its size is unknown: taken for landscape, as most cameras are.
    useLayoutEffect(() => {
        if (!isMaximized) {
            return undefined;
        }

        const update = () : void => {
            const decoder = streamRef.current?.stats().decoder;
            const isLandscape = !decoder?.displayWidth || decoder.displayWidth > decoder.displayHeight;
            setTurned(isLandscape && window.innerHeight > window.innerWidth);
        };
        update();
        window.addEventListener('resize', update);

        return () => window.removeEventListener('resize', update);
    }, [isMaximized]);

    useEffect(() => {
        if (!isMaximized) {
            return undefined;
        }

        const onKeyDown = (event: KeyboardEvent) : void => {
            if (event.key === 'Escape') {
                setMaximized(false);
            }
        };
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isMaximized]);

    return (
        <>
            {/* Black around the picture, as the legacy VLC view. */}
            <div
                ref={hostRef}
                style={{ position: 'absolute', inset: 0, background: '#000000', cursor: active ? 'zoom-in' : undefined }}
                onClick={
                    active
                        ? (event) => {
                            // The canvas is in the full-screen layer before this click reaches the
                            // page's body, where React would take it for a tap on the layer and close it.
                            event.stopPropagation();
                            setMaximized(true);
                        }
                        : undefined
                }
            />
            {isMaximized
                ? createPortal(
                    <div
                        role="button"
                        aria-label={`${tile.label ?? 'Video'}, full screen: tap to close`}
                        data-video-full-screen
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 1000,
                            boxSizing: 'border-box',
                            // The picture stays clear of the status bar and the camera island.
                            padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)',
                            background: '#000000',
                            cursor: 'zoom-out',
                        }}
                        onClick={() => setMaximized(false)}
                    >
                        {/* The safe area, sized for the turned picture's box (container query units). */}
                        <div style={{ position: 'relative', width: '100%', height: '100%', containerType: 'size' }}>
                            <div
                                ref={overlayRef}
                                style={
                                    isTurned
                                        ? {
                                            position: 'absolute',
                                            left: '50%',
                                            top: '50%',
                                            width: '100cqh',
                                            height: '100cqw',
                                            transform: 'translate(-50%, -50%) rotate(90deg)',
                                        }
                                        : { position: 'absolute', inset: 0 }
                                }
                            />
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </>
    );
}
